// src/pages/PortalInvoicesPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import portalApi from '../utils/portalApi';
import toast from 'react-hot-toast';

export default function PortalInvoicesPage() {
  const [invoices, setInvoices] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    portalApi.get('/portal/invoices')
      .then(res => setInvoices(res.data))
      .catch(() => toast.error('Failed to load invoices.'));
  }, []);

  if (invoices === null) return <div className="loading-page"><div className="spinner" /></div>;

  const filtered = filter === 'open'
    ? invoices.filter(i => !['paid','void'].includes(i.status))
    : filter === 'paid'
    ? invoices.filter(i => i.status === 'paid')
    : invoices;

  const totalOwed = invoices
    .filter(i => !['paid','void'].includes(i.status))
    .reduce((s, i) => s + parseFloat(i.balance_due || 0), 0);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Invoices</h1>
          <p className="page-subtitle">
            {totalOwed > 0
              ? <span style={{ color: 'var(--danger)' }}>${totalOwed.toFixed(2)} outstanding</span>
              : 'All paid up'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'open', 'paid'].map(f => (
            <button key={f}
              className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f === 'open' ? 'Open' : 'Paid'}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="table-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Type</th>
                <th>Issued</th>
                <th>Due</th>
                <th>Total</th>
                <th>Balance</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8}><div className="empty-state" style={{ padding: 32 }}>
                  <div className="empty-state-icon">📄</div>
                  <h3>No invoices</h3>
                </div></td></tr>
              ) : filtered.map(inv => {
                const isPayable = !['paid', 'void'].includes(inv.status) && parseFloat(inv.balance_due || 0) > 0;
                return (
                  <tr key={inv.id}>
                    <td className="td-primary td-mono">
                      <Link to={`/portal/invoices/${inv.id}`}>{inv.invoice_number}</Link>
                    </td>
                    <td style={{ textTransform: 'capitalize', fontSize: '0.85rem' }}>
                      {inv.invoice_type?.replace('_', ' ')}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{fmtDate(inv.issue_date)}</td>
                    <td style={{ fontSize: '0.85rem', color: isOverdue(inv) ? 'var(--danger)' : undefined }}>
                      {fmtDate(inv.due_date)}
                    </td>
                    <td>${parseFloat(inv.total_amount).toFixed(2)}</td>
                    <td style={{ fontWeight: isPayable ? 600 : 400, color: isPayable ? 'var(--danger)' : undefined }}>
                      ${parseFloat(inv.balance_due || 0).toFixed(2)}
                    </td>
                    <td><span className={`badge badge-${inv.status}`}>{inv.status?.replace('_', ' ')}</span></td>
                    <td>
                      {isPayable && inv.cardknox_payment_link && (
                        <a href={inv.cardknox_payment_link} target="_blank" rel="noreferrer"
                           className="btn btn-primary btn-sm">Pay Now</a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isOverdue(inv) {
  if (!inv.due_date || ['paid', 'void'].includes(inv.status)) return false;
  return new Date(inv.due_date) < new Date();
}
